// Unit helpers — DB stores grams (weight) and millimetres (length / head).
// UI shows kg with 2 decimals and cm with 1 decimal.

import type { MeasurementKind } from '@shared/constants.ts';

type KindMeta = {
  label: string;
  accent: string;
  unit: string;
  precision: number;
  toDisplay: (raw: number) => number;
  fromDisplay: (display: number) => number;
  /** Placeholder shown in the value input when empty. */
  placeholder: string;
  /** Free-text hint under the notes field. */
  notesPlaceholder: string;
  /** Whether the entry sheet should default to showing the time picker.
   *  Growth measurements snap to noon; temperature wants the actual time. */
  defaultShowTime: boolean;
};

export const KIND_META: Record<MeasurementKind, KindMeta> = {
  weight: {
    label: 'Weight',
    accent: 'accent',
    unit: 'kg',
    precision: 2,
    toDisplay: (g) => g / 1000,
    fromDisplay: (kg) => Math.round(kg * 1000),
    placeholder: '5.20',
    notesPlaceholder: 'e.g. at pediatrician, scale brand, what they were wearing',
    defaultShowTime: false,
  },
  length: {
    label: 'Length',
    accent: 'sleep-day',
    unit: 'cm',
    precision: 1,
    toDisplay: (mm) => mm / 10,
    fromDisplay: (cm) => Math.round(cm * 10),
    placeholder: '56.5',
    notesPlaceholder: 'e.g. at pediatrician, how measured',
    defaultShowTime: false,
  },
  head_circumference: {
    label: 'Head',
    accent: 'pump',
    unit: 'cm',
    precision: 1,
    toDisplay: (mm) => mm / 10,
    fromDisplay: (cm) => Math.round(cm * 10),
    placeholder: '38.0',
    notesPlaceholder: 'e.g. at pediatrician',
    defaultShowTime: false,
  },
  temperature: {
    label: 'Temperature',
    accent: 'danger',
    unit: '°C',
    precision: 1,
    toDisplay: (centideg) => centideg / 10,
    fromDisplay: (c) => Math.round(c * 10),
    placeholder: '37.5',
    notesPlaceholder: 'e.g. ear / armpit / forehead, symptoms',
    defaultShowTime: true,
  },
};

export function fmtMeasurement(kind: MeasurementKind, raw: number): string {
  const m = KIND_META[kind];
  // Round in integer space first to avoid IEEE-754 surprises like
  // (4.765).toFixed(2) === "4.76".
  const mult = 10 ** m.precision;
  const rounded = Math.round(m.toDisplay(raw) * mult) / mult;
  return `${rounded.toFixed(m.precision)} ${m.unit}`;
}

/** Same rounding rules as fmtMeasurement, but returns just the number string. */
export function fmtMeasurementValue(kind: MeasurementKind, raw: number): string {
  const m = KIND_META[kind];
  const mult = 10 ** m.precision;
  const rounded = Math.round(m.toDisplay(raw) * mult) / mult;
  return rounded.toFixed(m.precision);
}

export function fmtDelta(kind: MeasurementKind, raw: number): string {
  const m = KIND_META[kind];
  const sign = raw > 0 ? '+' : raw < 0 ? '−' : '';
  const v = Math.abs(m.toDisplay(raw));
  // Use grams / mm for tiny deltas — clearer than 0.05 kg
  if (kind === 'weight' && Math.abs(raw) < 1000) return `${sign}${Math.abs(raw)} g`;
  if ((kind === 'length' || kind === 'head_circumference') && Math.abs(raw) < 10) return `${sign}${Math.abs(raw)} mm`;
  if (kind === 'temperature') return `${sign}${v.toFixed(1)} °C`;
  return `${sign}${v.toFixed(m.precision)} ${m.unit}`;
}

export function ageDaysAt(birthDate: string, atSec: number): number {
  const [y, m, d] = birthDate.split('-').map(Number);
  const born = new Date(y, (m ?? 1) - 1, d ?? 1).getTime();
  const at = atSec * 1000;
  return Math.max(0, Math.floor((at - born) / 86_400_000));
}

export function fmtAge(days: number): string {
  if (days < 14) return `${days}d`;
  const w = Math.floor(days / 7);
  const r = days % 7;
  if (w < 12) return r ? `${w}w ${r}d` : `${w}w`;
  const months = Math.floor(days / 30.44);
  return `${months}mo`;
}
